var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from "classnames";
import { createContext, useCallback, useMemo } from "react";
import { useInRouterContext, useLocation, useNavigate, useParams, Link } from "react-router-dom";
export const RouterContext = createContext(null);
/**
 * Provides router-aware context with location, navigation,
 * params, and a URL generator with contextual navigation support.
 *
 * @example
 * ```tsx
 * <RouterContextProvider>
 *   <App />
 * </RouterContextProvider>
 * ```
 */
export const RouterContextProvider = ({ children }) => {
    const hasRouter = useInRouterContext();
    const location = hasRouter ? useLocation() : null;
    const baseNavigate = hasRouter ? useNavigate() : null;
    const params = hasRouter ? useParams() : null;
    // Comprehensive URL generation function
    const generateURL = useCallback((options) => {
        var _a;
        const { path, searchParams = {}, removeParams = [], mergeParams = true } = options;
        const currentLocation = window.location || location;
        // Use provided path or current location
        const basePath = path || ((_a = currentLocation === null || currentLocation === void 0 ? void 0 : currentLocation.pathname) !== null && _a !== void 0 ? _a : '');
        // Start with current search params if merging, otherwise start fresh
        const currentParams = new URLSearchParams((currentLocation === null || currentLocation === void 0 ? void 0 : currentLocation.search) || '');
        const newParams = mergeParams ? new URLSearchParams(currentParams) : new URLSearchParams();
        // Remove specified parameters
        removeParams.forEach(param => newParams.delete(param));
        // Add/update new parameters
        Object.entries(searchParams).forEach(([key, value]) => {
            if (value === null || value === undefined) {
                newParams.delete(key);
            }
            else if (typeof value === 'object') {
                newParams.set(key, JSON.stringify(value));
            }
            else {
                newParams.set(key, String(value));
            }
        });
        const searchString = newParams.toString();
        return basePath + (searchString ? `?${searchString}` : '');
    }, [hasRouter]);
    // Enhanced navigate function
    const navigate = useCallback((toOrContextual, options) => {
        if (!baseNavigate)
            return;
        // Check if it's contextual navigation
        if (typeof toOrContextual === 'object' && 'embeddedUrl' in toOrContextual) {
            const { embeddedUrl, standaloneUrl, isEmbedded, event, openInNewTab } = toOrContextual;
            const isCtrlClick = event.ctrlKey || event.metaKey;
            if (isCtrlClick || openInNewTab) {
                // Ctrl/Cmd + click: open standalone URL in new tab
                window.open(standaloneUrl, '_blank');
            }
            else {
                // Regular click: use embedded URL if embedded, otherwise standalone
                const targetUrl = isEmbedded ? embeddedUrl : standaloneUrl;
                baseNavigate(targetUrl);
            }
            return;
        }
        // Regular navigation
        baseNavigate(toOrContextual, options);
    }, [baseNavigate]);
    const value = useMemo(() => ({
        location,
        navigate: navigate || null,
        params,
        hasRouter,
        generateURL,
    }), [location, navigate, params, hasRouter, generateURL]);
    return (_jsx(RouterContext.Provider, { value: value, children: children }));
};
/**
 * @export
 * A safe link that:
 * - Uses `react-router` `<Link>` when inside a router.
 * - Falls back to `<a>` otherwise.
 * - Supports cmd/ctrl+click to open in new tab
 *
 * @example
 * ```tsx
 * <SafeLink to="/settings">Settings</SafeLink>
 * ```
 *
 * @example
 * ```tsx
 * <SafeLink to="https://example.com" target="_blank">External</SafeLink>
 * ```
 */
export const SafeLink = (_a) => {
    var { to, replace, children, className, target, onClick } = _a, rest = __rest(_a, ["to", "replace", "children", "className", "target", "onClick"]);
    const hasRouter = useInRouterContext();
    // Handle cmd/ctrl+click to open in new tab
    const handleClick = useCallback((e) => {
        const isCtrlClick = e.ctrlKey || e.metaKey;
        if (isCtrlClick) {
            e.preventDefault();
            window.open(to, '_blank');
            return;
        }
        // Call original onClick if provided
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    }, [to, onClick]);
    // Use native <a> tag when target is specified (e.g., target="_blank")
    // React Router's Link doesn't handle target properly
    if (hasRouter && (!target || target !== '_blank')) {
        return (_jsx(Link, Object.assign({ to: to, replace: replace, className: className, onClick: handleClick }, rest, { children: children })));
    }
    return (_jsx("a", Object.assign({ href: to, className: classNames('uxpcore_safelink__anchortag', className), onClick: handleClick }, rest, { target: target || '_self', children: children })));
};
